import axios from "axios"
import clsx from "clsx"
import { useTranslation } from "next-i18next"
import { useContext, useState } from "react"
import { newCloseIcon } from "../../comps/ai-tools/icons"
import Button from "../../comps/button"
import { CustomThemedResource } from "../../comps/image"
import {
	ErrorMessage,
	SuccessMessage,
} from "../../comps/message"
import { Textarea } from "../../comps/textarea"
import { assetUrl } from "../../utils/cdn"
import { NotificationContext } from "../../utils/notification"
import { useShortCut } from "@kartyom/shared-components"

export function FeedbackMessage(props: {
	action: () => void
}) {
	const { t } = useTranslation()

	return (
		<div
			className={clsx(
				"flex w-full gap-[18px] rounded-[12px] bg-color-popup-cell p-[16px]",
				"relative mt-[100px] flex-col tablet:gap-0",
			)}>
			<div className="flex w-full items-start justify-between tablet:items-center">
				<div className="flex flex-col gap-[6px]">
					<span className="text-[18px] font-500 text-blue-700">
						{t("txt_give_feedback")}
					</span>

					<span className="text-[16px] font-400 text-blue-700">
						{t("txt_do_you_like_result")}
					</span>
				</div>

				<button
					onClick={props.action}
					className={clsx(
						"h-[24px] w-[24px] [--icon-color:var(--color-blue-400)]",
						"hover:[--icon-color:var(--color-blue-500)]",
					)}>
					{newCloseIcon()}
				</button>
			</div>

			<button
				onClick={() => {
					const feedback = document.getElementById(
						"feedback-message-background",
					)
					if (feedback) {
						feedback.style.display = "flex"
					}
				}}
				className={clsx(
					"flex w-full items-center justify-center py-[12px] outline-none",
					"rounded-[6px] border border-blue-200 tablet:w-[151px]",
					"tablet:absolute tablet:top-1/2 tablet:-translate-y-1/2",
					" transition-all duration-300 hover:bg-blue-100 tablet:right-[52px]",
				)}>
				<span className="text-[16px] font-500 leading-[20px] text-blue-800">
					{t("txt_leave_feedback")}
				</span>
			</button>
		</div>
	)
}

export function FeedbackMessagePopup(props: {
	id: number,
changeShowRating: () => void
}) {
	const { id, changeShowRating } = props
	const { t } = useTranslation()
	const { notify } = useContext(NotificationContext)
	const [feedbackText, setFeedbackText] = useState("")
	const [selectedEmotion, setSelectedEmotion] = useState<
		null | number
	>(null)

	useShortCut({
		shortCut: "Escape",
		handler: () => {
			const feedback = document.getElementById(
				"feedback-message-background",
			)
			if (feedback) {
				feedback.style.display = "none"
			}
		},
	})

	return (
		<div
			id="feedback-message-background"
			className={clsx(
				"fixed left-0 top-0 z-[105] h-[100svh] w-screen bg-color-popup",
				"flex items-end tablet:items-center tablet:justify-center",
			)}
			style={{
				display: "none",
			}}
			onClick={(e) => {
				if (e.currentTarget) {
					if (e.currentTarget.id !== "feedback-message") {
						const feedback = document.getElementById(
							"feedback-message-background",
						)
						if (feedback) {
							feedback.style.display = "none"
						}
					}
				}
			}}>
			<div
				onClick={(e) => e.stopPropagation()}
				id="feedback-message"
				className={clsx(
					"flex w-full flex-col bg-color-popup-cell pt-[24px]",
					"h-[555px] gap-[24px] rounded-t-[12px] tablet:w-[520px]",
					"tablet:h-auto tablet:rounded-b-[10px] tablet:rounded-t-[10px]",
					"desktop:relative",
				)}>
				<div className="flex flex-col items-center gap-[12px] px-[24px]">
					<span className="text-[26px] font-700 text-blue-900 tablet:text-[28px]">
						{t("txt_give_feedback")}
					</span>

					<span className="text-[18px] font-400 text-blue-700 tablet:text-[20px]">
						{t("txt_feedback_matters_you")}
					</span>
				</div>

				<div className="mx-[24px] h-[1px] w-[calc(100%-48px)] bg-color-separator" />

				<div className="flex w-full flex-col gap-[18px]">
					<div className="flex flex-col gap-[12px] px-[24px]">
						<span className="text-[18px] font-400 text-blue-700">
							{t("txt_rate_your_experience")}
						</span>

						<div className="flex items-center justify-between">
							{rateEmotions.map((elem) => (
								<Emotion
									key={elem.name}
									name={elem.name}
									icon={elem.icon}
									selected={selectedEmotion === elem.rate}
									setSelected={() =>
										setSelectedEmotion(elem.rate)
									}
								/>
							))}
						</div>
					</div>

					<div className="flex flex-col gap-[12px] px-[24px] pt-[10px]">
						<p className="text-[18px] font-400 leading-[24px] text-blue-700">
							{t("txt_what_you_want_to_change")}
						</p>

						<div
							className={clsx(
								"h-[95px] w-full rounded-[8px] border border-color-separator",
								"bg-color-surface-cell px-[12px] py-[8px]",
							)}>
							<Textarea
								value={feedbackText}
								setValue={setFeedbackText}
								placeholder={t("txt_feedback_placeholder")}
								background="bg-color-surface-cell"
							/>
						</div>
					</div>

					<div
						className={clsx(
							"flex w-full px-[24px] tablet:border-t tablet:bg-color-popup-cell tablet:py-[24px]",
							"tablet:rounded-b-[10px] tablet:border-color-separator",
						)}>
						<Button
							text={t("txt_send_feedback")}
							primary
							className="w-full"
							disabled={
								selectedEmotion === null ||
								feedbackText === ""
							}
							onClick={async () => {
								await axios
									.post("/api/wizard-rate-session", {
										session_id: id,
										rating: selectedEmotion,
										message: feedbackText,
									})
									.then(() =>
										notify(
											<SuccessMessage>
												{t("txt_thanks_feedback")}
											</SuccessMessage>,
										),
									)
									.catch(() =>
										notify(
											<ErrorMessage>
												{t("txt_something_wrong")}
											</ErrorMessage>,
										),
									)

								setSelectedEmotion(null)
								setFeedbackText("")
								changeShowRating()
								const feedback = document.getElementById(
									"feedback-message-background",
								)
								if (feedback) {
									feedback.style.display = "none"
								}
							}}
						/>
					</div>
				</div>

				<button
					onClick={() => {
						const feedback = document.getElementById(
							"feedback-message-background",
						)
						if (feedback) {
							feedback.style.display = "none"
						}
					}}
					className={clsx(
						"absolute right-[-40px] top-0 hidden bg-color-surface desktop:flex",
						"h-[32px] w-[32px] items-center justify-center rounded-full",
						"border border-color-separator",
					)}>
					<CustomThemedResource
						format="svg"
						source="/general/close"
					/>
				</button>
			</div>
		</div>
	)
}

function Emotion(props: {
	name: string
	icon: string
	selected: boolean
	setSelected: () => void
}) {
	const { name, icon, selected, setSelected } = props
	const { t } = useTranslation()

	return (
		<button
			onClick={setSelected}
			className="group flex flex-col items-center gap-[8px]">
			<div
				className={clsx(
					selected
						? "border-primary-400 bg-primary-100"
						: "border-color-surface bg-color-surface",
					"rounded-[8px] transition-colors duration-300 tablet:rounded-[12px]",
					"border-[1.5px] p-[7px] group-hover:bg-primary-100 tablet:p-[11px]",
				)}>
				<img
					src={assetUrl(icon)}
					alt="emotion"
					className={clsx(
						"h-[40px] w-[40px] transition-all duration-300",
						"group-hover:scale-105 tablet:h-[54px] tablet:w-[54px]",
					)}
				/>
			</div>

			<span
				className={clsx(
					selected ? "text-primary-500" : "text-blue-500",
					"text-[13px] font-400 leading-[22px] tracking-[0.24px]",
					"transition-all duration-300",
				)}>
				{t(`lbl_${name}`)}
			</span>
		</button>
	)
}

const rateEmotions = [
	{
		name: "awful",
		icon: "/general/awful.png",
		rate: 1,
	},
	{
		name: "poor",
		icon: "/general/poor.png",
		rate: 2,
	},
	{
		name: "neutral",
		icon: "/general/neutral.png",
		rate: 3,
	},
	{
		name: "good",
		icon: "/general/good.png",
		rate: 4,
	},
	{
		name: "exellent",
		icon: "/general/exellent.png",
		rate: 5,
	},
]
